import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  PlusCircleIcon,
  TrashIcon,
  PencilSquareIcon,
  TagIcon,
  ArrowTopRightOnSquareIcon,
  DocumentDuplicateIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import '../styles/DetailView.css';
import './ComponentsModal.css';
import DetailCrumb from './DetailCrumb';

const COMP_TYPES = ['cpu', 'ram', 'nic', 'hba', 'gpu', 'raid', 'psu', 'bmc', 'other'];
const COMP_LABELS = {
  cpu:  'Procesador (CPU)',
  ram:  'Memoria RAM',
  nic:  'Tarjeta de red (NIC)',
  hba:  'HBA (Fibre Channel)',
  gpu:  'GPU',
  raid: 'Controladora RAID',
  psu:  'Fuente de alimentación',
  bmc:  'BMC / iLO (gestión)',
  other: 'Otro',
};
const PORT_TYPES = ['rj45', 'sfp', 'sfp+', 'qsfp', 'qsfp28', 'fc'];
// Tipos de componente que pueden tener puertos fisicos (NIC/HBA/PSU no).
const COMP_TYPES_WITH_PORTS = ['nic', 'hba', 'bmc'];
const PORT_SPEEDS = ['', '100M', '1G', '2.5G', '10G', '25G', '40G', '100G'];
const TEAM_MODES = ['active-backup', 'lacp', 'switch-independent', 'static'];

const EMPTY_TEAM = () => ({
  name: '',
  mode: 'lacp',
  member_port_ids: [],
  vlan_id: '',
  mtu: '',
  ip_mode: '',
  ip_address: '',
  prefix: '',
  gateway: '',
  dns: '',
});

function ServerComponentsPanel({
  server,
  companyId,
  onClose,
  onUpdated,
  getStorage,
  updateStorage,
  getComponents,
  createComponent,
  updateComponent,
  deleteComponent,
  createComponentPort,
  updateComponentPort,
  deleteComponentPort,
  entityLabel = 'Servidor',
  allowCopy = true,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('components');

  const [components, setComponents] = useState([]);
  const [editComp, setEditComp] = useState(null);
  const [editPort, setEditPort] = useState(null);
  const [deleteComp, setDeleteComp] = useState(null);
  const [deletePort, setDeletePort] = useState(null);

  const [copyModal, setCopyModal] = useState(false);
  const [notice, setNotice] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const toggleGroup = (type) => setCollapsedGroups(prev => {
    const next = new Set(prev);
    if (next.has(type)) next.delete(type); else next.add(type);
    return next;
  });

  const [storageMeta, setStorageMeta] = useState({ inventory_date: '', taken_by: '' });
  const [storageData, setStorageData] = useState({});
  const [teams, setTeams] = useState([]);
  const [teamModal, setTeamModal] = useState({ open: false, team: null, idx: null });

  const fetchComponents = useMemo(() => getComponents || api.getComponents.bind(api), [getComponents]);
  const saveComponent = useMemo(() => createComponent || api.createComponent.bind(api), [createComponent]);
  const editComponent = useMemo(() => updateComponent || api.updateComponent.bind(api), [updateComponent]);
  const removeComponent = useMemo(() => deleteComponent || api.deleteComponent.bind(api), [deleteComponent]);
  const saveComponentPort = useMemo(() => createComponentPort || api.createComponentPort.bind(api), [createComponentPort]);
  const editComponentPort = useMemo(() => updateComponentPort || api.updateComponentPort.bind(api), [updateComponentPort]);
  const removeComponentPort = useMemo(() => deleteComponentPort || api.deleteComponentPort.bind(api), [deleteComponentPort]);

  const fetchAll = useCallback(async () => {
    if (!server?.id || !companyId) return;
    setLoading(true);
    setError('');
    try {
      const fetchStorage = getStorage || api.getServerStorage.bind(api);
      const [cr, sr] = await Promise.all([
        fetchComponents(server.id, companyId),
        fetchStorage(server.id, companyId),
      ]);
      const comps = Array.isArray(cr?.data) ? cr.data : [];
      setComponents(comps);

      const rec = sr?.data || null;
      const meta = {
        inventory_date: rec?.inventory_date || '',
        taken_by: rec?.taken_by || '',
      };
      const data = rec?.data && typeof rec.data === 'object' ? rec.data : {};
      setStorageMeta(meta);
      setStorageData(data);
      setTeams(Array.isArray(data.network_teams) ? data.network_teams : []);
    } catch (e) {
      setError(e.message || 'Error cargando componentes');
    } finally {
      setLoading(false);
    }
  }, [server?.id, companyId, fetchComponents, getStorage]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const nicPorts = useMemo(() => {
    const ports = [];
    components
      .filter(c => c.component_type === 'nic')
      .forEach(c => {
        (c.ports || []).forEach(p => {
          ports.push({
            id: p.id,
            compId: c.id,
            componentName: `${c.manufacturer || ''} ${c.model || ''}`.trim() || `NIC #${c.id}`,
            label: p.label || `P${p.port_number}`,
            speed: p.speed || '',
            portType: p.port_type,
            mac: p.mac_address || '',
          });
        });
      });
    return ports;
  }, [components]);

  const portTeamMap = useMemo(() => {
    const map = new Map();
    teams.forEach((t, idx) => {
      (t.member_port_ids || []).forEach(pid => map.set(Number(pid), { name: t.name, mode: t.mode, idx }));
    });
    return map;
  }, [teams]);

  const handleSaveComp = async (formData) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      // Extract auto-ports template before sending to backend
      const { _autoPortsTemplate, ...cleanData } = formData;
      let newCompId = null;
      if (editComp?.id) {
        await editComponent(server.id, editComp.id, cleanData, companyId);
      } else {
        const res = await saveComponent(server.id, cleanData, companyId);
        newCompId = res?.data?.id;
      }
      // Auto-create ports for new NIC/HBA if template defined
      if (newCompId && _autoPortsTemplate && _autoPortsTemplate.count > 0) {
        const { count, port_type, speed } = _autoPortsTemplate;
        for (let i = 0; i < count; i++) {
          try {
            await saveComponentPort(server.id, newCompId, {
              port_number: i + 1,
              label: `P${i + 1}`,
              port_type,
              speed: speed || '',
            }, companyId);
          } catch (pe) {
            // Non-blocking — component created OK, just log the port error
            console.warn('Auto-port creation error:', pe.message);
          }
        }
      }
      setEditComp(null);
      await fetchAll();
      onUpdated?.();
    } catch (e) {
      setError(e.message || 'Error guardando componente');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicateComp = async (comp) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      // El slot identifica al componente dentro del equipo (índice único
      // element_id + slot + component_type), así que la copia se crea SIN slot:
      // si se reenviara el mismo, PostgreSQL rechaza el INSERT.
      const { id, serial_number, asset_tag, element_id, slot, ports, created_at, updated_at, _autoPortsTemplate, ...payload } = comp;
      const res = await saveComponent(
        server.id,
        { ...payload, slot: null, serial_number: null, asset_tag: null },
        companyId,
      );
      const newCompId = res?.data?.id;
      // Re-create ports for NIC/HBA (no MAC/WWN — those are unique per port)
      if (newCompId && Array.isArray(ports)) {
        for (const port of ports) {
          try {
            await saveComponentPort(server.id, newCompId, {
              port_number: port.port_number,
              label: port.label || `P${port.port_number}`,
              port_type: port.port_type,
              speed: port.speed || '',
            }, companyId);
          } catch { /* non-blocking */ }
        }
      }
      setNotice('Componente duplicado sin slot: asígnale el slot y el nº de serie con ✏️.');
      await fetchAll();
      onUpdated?.();
    } catch (e) {
      setError(e.message || 'Error duplicando componente');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteComp = async () => {
    if (!deleteComp) return;
    setSaving(true);
    try {
      await removeComponent(server.id, deleteComp.id, companyId);
      setDeleteComp(null);
      await fetchAll();
      onUpdated?.();
    } catch (e) {
      setError(e.message || 'Error eliminando componente');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePort = async (compId, portId, formData) => {
    setSaving(true);
    setError('');
    try {
      if (portId) await editComponentPort(server.id, compId, portId, formData, companyId);
      else await saveComponentPort(server.id, compId, formData, companyId);
      setEditPort(null);
      await fetchAll();
      onUpdated?.();
    } catch (e) {
      setError(e.message || 'Error guardando puerto');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePort = async () => {
    if (!deletePort) return;
    setSaving(true);
    try {
      await removeComponentPort(server.id, deletePort.compId, deletePort.port.id, companyId);
      setDeletePort(null);
      await fetchAll();
      onUpdated?.();
    } catch (e) {
      setError(e.message || 'Error eliminando puerto');
    } finally {
      setSaving(false);
    }
  };

  const saveTeams = async (nextTeams) => {
    setSaving(true);
    setError('');
    try {
      const merged = { ...storageData, network_teams: nextTeams };
      const saveStorage = updateStorage || api.updateServerStorage.bind(api);
      await saveStorage(server.id, {
        inventory_date: storageMeta.inventory_date,
        taken_by: storageMeta.taken_by,
        data: merged,
      }, companyId);
      setStorageData(merged);
      setTeams(nextTeams);
      onUpdated?.();
    } catch (e) {
      setError(e.message || 'Error guardando teams');
    } finally {
      setSaving(false);
    }
  };

  const openTeamModal = (team, idx) => setTeamModal({ open: true, team, idx });
  const closeTeamModal = () => setTeamModal({ open: false, team: null, idx: null });

  const saveTeamModal = async (teamDraft) => {
    const next = [...teams];
    // keep NIC port in only one team
    const selected = new Set((teamDraft.member_port_ids || []).map(Number));
    next.forEach((t, i) => {
      if (teamModal.idx !== i) {
        t.member_port_ids = (t.member_port_ids || []).filter(pid => !selected.has(Number(pid)));
      }
    });

    if (teamModal.idx == null) next.push(teamDraft);
    else next[teamModal.idx] = teamDraft;

    await saveTeams(next);
    closeTeamModal();
  };

  const removeTeam = async (idx) => {
    const next = teams.filter((_, i) => i !== idx);
    await saveTeams(next);
  };

  if (loading) {
    return (
      <div className="dv-overlay">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
          <div className="spinner-border" role="status" />
        </div>
      </div>
    );
  }

  return (
    <div className="dv-overlay">
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon /> {entityLabel}
        </button>
        <DetailCrumb label={`${server?.name || server?.hostname || ''} · Componentes`} />
        <div className="dv-topbar-spacer" />
        {error && <span style={{ color: '#dc2626', fontSize: '0.78rem' }}>⚠ {error}</span>}
        {!error && notice && (
          <span
            title={notice}
            style={{
              color: '#d97706',
              fontSize: '0.78rem',
              maxWidth: 320,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            ℹ {notice}
          </span>
        )}
        <div className="dv-topbar-actions">
          {saving && <span className="dv-status-badge" style={{ background: '#fef3c7', color: '#92400e' }}>⏳ Guardando…</span>}
          <button className="dv-btn dv-btn-secondary" type="button" onClick={fetchAll} disabled={saving}>Recargar</button>
          {allowCopy && components.length > 0 && (
            <button className="dv-btn dv-btn-primary" type="button" onClick={() => setCopyModal(true)} disabled={saving} title="Copiar componentes a otro servidor">
              <ArrowTopRightOnSquareIcon style={{ width: 15, height: 15 }} /> Copiar a servidor
            </button>
          )}
        </div>
      </div>

      <div className="dv-smartbar">
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('components')}>
          <span className="dv-smart-btn-value" style={{ color: '#2563eb' }}>{components.length}</span>
          <span className="dv-smart-btn-label">Componentes</span>
        </button>
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('components')}>
          <span className="dv-smart-btn-value" style={{ color: '#0f766e' }}>{nicPorts.length}</span>
          <span className="dv-smart-btn-label">Puertos NIC</span>
        </button>
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('teams')}>
          <span className="dv-smart-btn-value" style={{ color: '#7c3aed' }}>{teams.length}</span>
          <span className="dv-smart-btn-label">Teams</span>
        </button>
      </div>

      <div className="dv-body">
        <div className="dv-main" style={{ overflowY: 'auto' }}>
          <div className="dv-tabs-nav">
            <button type="button" className={`dv-tab-btn${activeTab === 'components' ? ' active' : ''}`} onClick={() => setActiveTab('components')}>Componentes</button>
            <button type="button" className={`dv-tab-btn${activeTab === 'teams' ? ' active' : ''}`} onClick={() => setActiveTab('teams')}>Teams de red</button>
          </div>

          {activeTab === 'components' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div className="dv-section-title" style={{ margin: 0 }}>Componentes de {entityLabel.toLowerCase()}</div>
                  <button className="dv-btn dv-btn-primary" type="button" onClick={() => setEditComp({})}>
                    <PlusCircleIcon style={{ width: 16, height: 16 }} /> Añadir componente
                  </button>
                </div>

                {components.length === 0 ? (
                  <div style={{ color: '#94a3b8', padding: '2rem', textAlign: 'center' }}>Sin componentes instalados.</div>
                ) : (
                  // ── Grouped by component type ──
                  COMP_TYPES.filter(type => components.some(c => c.component_type === type))
                    .map(type => {
                      const group = components.filter(c => c.component_type === type);
                      const isCollapsed = collapsedGroups.has(type);
                      const ICONS = { psu: '⚡', cpu: '🔲', ram: '💾', nic: '🔌', hba: '🔗', gpu: '🎮', raid: '💿', bmc: '🔧', other: '🧩' };
                      return (
                        <div key={type} style={{ marginBottom: 16 }}>
                          {/* Group header */}
                          <div
                            role="button"
                            onClick={() => toggleGroup(type)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                              padding: '6px 10px', borderRadius: 6, marginBottom: isCollapsed ? 0 : 6,
                              background: 'var(--bg-toolbar)', border: '1px solid var(--border)', userSelect: 'none',
                            }}
                          >
                            {isCollapsed
                              ? <ChevronRightIcon style={{ width: 14, height: 14, color: 'var(--text-muted)', flexShrink: 0 }} />
                              : <ChevronDownIcon  style={{ width: 14, height: 14, color: 'var(--text-muted)', flexShrink: 0 }} />
                            }
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>
                              {ICONS[type]} {COMP_LABELS[type] || type}
                            </span>
                            <span style={{
                              marginLeft: 4, fontSize: 11, fontWeight: 700,
                              background: 'rgba(37,99,235,.2)', color: '#93c5fd',
                              borderRadius: 10, padding: '1px 7px',
                            }}>{group.length}</span>
                            {type === 'ram' && (() => {
                              const total = group.reduce((s, c) => { try { return s + (JSON.parse(c.notes || '{}').capacity_gb || 0); } catch { return s; } }, 0);
                              const slots = (() => { try { return JSON.parse((group[0] || {}).notes || '{}').total_slots; } catch { return 0; } })();
                              return <>
                                {total > 0 && <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 700, background: 'rgba(22,163,74,.18)', color: '#4ade80', borderRadius: 10, padding: '1px 7px' }}>{total} GB</span>}
                                {slots > 0 && <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--text-muted)' }}>{group.length}/{slots} slots</span>}
                              </>;
                            })()}
                            {type === 'cpu' && (() => {
                              const cores = group.reduce((s, c) => { try { return s + (JSON.parse(c.notes || '{}').cores || 0); } catch { return s; } }, 0);
                              const threads = group.reduce((s, c) => { try { return s + (JSON.parse(c.notes || '{}').threads || 0); } catch { return s; } }, 0);
                              return cores > 0 ? <span style={{ marginLeft: 4, fontSize: 11, fontWeight: 700, background: 'rgba(217,119,6,.12)', color: '#d97706', borderRadius: 10, padding: '1px 7px' }}>{cores}C / {threads}T</span> : null;
                            })()}
                          </div>

                          {/* Group items */}
                          {!isCollapsed && group.map(comp => (
                    <div key={comp.id} className="cmp-card" style={{ marginBottom: 8 }}>
                      <div className="cmp-card-header">
                        <span className="cmp-card-icon">{({ psu: '⚡', cpu: '🔲', ram: '💾', nic: '🔌', hba: '🔗', gpu: '🎮', raid: '💿' })[comp.component_type] || '🧩'}</span>
                        <div className="cmp-card-info">
                          <strong>{comp.manufacturer || ''} {comp.model || <em>Sin modelo</em>}</strong>
                          <span>
                            {COMP_LABELS[comp.component_type] || comp.component_type}{comp.slot ? ` · ${comp.slot}` : ''}
                            {comp.component_type === 'psu' && comp.power_watts ? ` · ${comp.power_watts}W` : ''}
                            {comp.component_type === 'psu' && comp.connector_type ? ` · ${comp.connector_type}` : ''}
                            {comp.component_type === 'psu' && comp.efficiency ? ` · ${comp.efficiency}` : ''}
                            {comp.component_type === 'ram' && (() => { try { const d = JSON.parse(comp.notes || '{}'); return `${d.capacity_gb ? ` · ${d.capacity_gb} GB` : ''}${d.type ? ` · ${d.type}` : ''}${d.ecc ? ' · ECC' : ''}${d.speed_mhz ? ` · ${d.speed_mhz} MHz` : ''}`; } catch { return ''; } })()}
                            {comp.component_type === 'cpu' && (() => { try { const d = JSON.parse(comp.notes || '{}'); return `${d.cores ? ` · ${d.cores}C/${d.threads}T` : ''}${d.speed_mhz ? ` · ${d.speed_mhz} MHz` : ''}${d.hyperthreading ? ' · HT' : ''}`; } catch { return ''; } })()}
                          </span>
                        </div>
                        <span className={`cmp-status cmp-status--${comp.status}`}>{comp.status}</span>
                        <div className="cmp-card-actions">
                          {COMP_TYPES_WITH_PORTS.includes(comp.component_type) && (
                            <button className="cmp-icon-btn" title="Añadir puerto" onClick={() => setEditPort({ compId: comp.id, port: {} })}>+ Puerto</button>
                          )}
                          <button
                            className="cmp-icon-btn"
                            title="Duplicar (sin nº serie)"
                            onClick={() => handleDuplicateComp(comp)}
                            disabled={saving}
                          >
                            <DocumentDuplicateIcon style={{ width: 14, height: 14 }} />
                          </button>
                          <button className="cmp-icon-btn" title="Editar" onClick={() => setEditComp(comp)}>✏️</button>
                          <button className="cmp-icon-btn cmp-icon-btn--del" title="Eliminar" onClick={() => setDeleteComp(comp)}>🗑️</button>
                        </div>
                      </div>

                      {COMP_TYPES_WITH_PORTS.includes(comp.component_type) && (
                        comp.ports && comp.ports.length > 0 ? (
                        <table className="cmp-ports-table">
                          <thead>
                            <tr><th>#</th><th>Label</th><th>Tipo</th><th>Velocidad</th><th>MAC / WWN</th><th>Estado</th><th>Team</th><th>Conexión activa</th><th></th></tr>
                          </thead>
                          <tbody>
                            {comp.ports.map(port => (
                              <tr key={port.id}>
                                <td><strong>{port.port_number}</strong></td>
                                <td>{port.label || '—'}</td>
                                <td><span className="cmp-port-type">{port.port_type}</span></td>
                                <td>{port.speed || '—'}</td>
                                <td><code className="cmp-mac">{port.mac_address || port.wwn || '—'}</code></td>
                                <td>
                                  {port.is_active
                                    ? <span style={{ color: '#6ee7b7', fontSize: '0.8rem' }}>Up</span>
                                    : <span style={{ color: '#f87171', fontSize: '0.8rem' }}>Disabled</span>}
                                </td>
                                <td>
                                  {portTeamMap.get(Number(port.id)) ? (
                                    <span className="cmp-team-badge">
                                      {portTeamMap.get(Number(port.id)).name}
                                    </span>
                                  ) : (
                                    <span style={{ color: 'var(--text-muted)' }}>—</span>
                                  )}
                                </td>
                                <td>
                                  {port.connection ? (
                                    <span className="cmp-port-connected">
                                      ● {port.connection.other_end?.element_name || ''}
                                      {port.connection.other_end?.port_number != null && <> P{port.connection.other_end.port_number}</>}
                                    </span>
                                  ) : <span className="cmp-port-free">○ Libre</span>}
                                </td>
                                <td>
                                  <div style={{ display: 'flex', gap: 4 }}>
                                    <button className="cmp-icon-btn" onClick={() => setEditPort({ compId: comp.id, port })}>✏️</button>
                                    <button className="cmp-icon-btn cmp-icon-btn--del" onClick={() => setDeletePort({ compId: comp.id, port })}>🗑️</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="cmp-no-ports">Sin puertos.</div>
                      )
                      )}
                    </div>
                  ))}
                        </div>
                      );
                    })
                )}
              </div>
            </div>
          )}

          {activeTab === 'teams' && (
            <div className="dv-tab-pane">
              <div className="dv-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div className="dv-section-title" style={{ margin: 0 }}>Teaming de NIC (pre-IPAM)</div>
                  <button className="dv-btn dv-btn-primary" type="button" onClick={() => openTeamModal(EMPTY_TEAM(), null)}>
                    <PlusCircleIcon style={{ width: 16, height: 16 }} /> Crear team
                  </button>
                </div>

                {nicPorts.length === 0 && (
                  <div style={{ color: 'var(--text-muted)', padding: '1rem 0' }}>No hay puertos NIC para asignar.</div>
                )}

                {teams.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Sin teams definidos.</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {teams.map((t, idx) => (
                      <div key={idx} style={{ width: 280, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                          <strong style={{ color: 'var(--text-primary)', fontSize: '0.9rem' }}>{t.name || `team-${idx + 1}`}</strong>
                          <span style={{ fontSize: '0.7rem', background: 'rgba(99,102,241,.2)', color: '#a5b4fc', borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap', flexShrink: 0 }}>{t.mode}</span>
                        </div>
                        <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 6 }}>
                          {(t.member_port_ids || []).length} miembro(s)
                          {t.vlan_id ? ` · VLAN ${t.vlan_id}` : ''}
                          {t.ip_mode ? ` · ${t.ip_mode.toUpperCase()}` : ''}
                          {t.ip_address ? ` · ${t.ip_address}${t.prefix ? `/${t.prefix}` : ''}` : ''}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>
                          {(t.member_port_ids || []).map(pid => {
                            const p = nicPorts.find(np => Number(np.id) === Number(pid));
                            return p ? `${p.componentName} / ${p.label}` : `Port ${pid}`;
                          }).join(', ') || 'Sin miembros'}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                          <button className="cmp-icon-btn" title="Editar team" onClick={() => openTeamModal(t, idx)}><PencilSquareIcon style={{ width: 13, height: 13 }} /></button>
                          <button className="cmp-icon-btn cmp-icon-btn--del" title="Eliminar team" onClick={() => removeTeam(idx)}><TrashIcon style={{ width: 13, height: 13 }} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <aside className="dv-sidebar">
          <div className="dv-sidebar-header"><button className="dv-chatter-btn active" type="button">Resumen</button></div>
          <div className="dv-chatter-body">
            <div className="dv-chatter-entry">
              <div className="dv-avatar">HW</div>
              <div className="dv-chatter-meta">
                <span className="dv-chatter-who">Componentes</span>
                <span className="dv-chatter-text">{components.length} total</span>
              </div>
            </div>
            <div className="dv-chatter-entry">
              <div className="dv-avatar"><TagIcon style={{ width: 14, height: 14 }} /></div>
              <div className="dv-chatter-meta">
                <span className="dv-chatter-who">Teams</span>
                <span className="dv-chatter-text">{teams.length} definidos</span>
              </div>
            </div>
            <div className="dv-chatter-entry">
              <div className="dv-avatar">NIC</div>
              <div className="dv-chatter-meta">
                <span className="dv-chatter-who">Puertos NIC</span>
                <span className="dv-chatter-text">{nicPorts.length} detectados</span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {allowCopy && copyModal && (
        <CopyToServerModal
          sourceServer={server}
          components={components}
          companyId={companyId}
          onClose={() => setCopyModal(false)}
          onDone={fetchAll}
        />
      )}

      {editComp !== null && (
        <ComponentForm comp={editComp} companyId={companyId} onSave={handleSaveComp} onCancel={() => setEditComp(null)} saving={saving} />
      )}

      {editPort !== null && (
        <PortForm editPort={editPort} onSave={handleSavePort} onCancel={() => setEditPort(null)} saving={saving} />
      )}

      {deleteComp && (
        <ConfirmDialog
          title="¿Eliminar componente?"
          message={`Se eliminará ${deleteComp.manufacturer || ''} ${deleteComp.model || ''} y sus puertos.`}
          saving={saving}
          onCancel={() => setDeleteComp(null)}
          onConfirm={handleDeleteComp}
        />
      )}

      {deletePort && (
        <ConfirmDialog
          title="¿Eliminar puerto?"
          message={`Puerto ${deletePort.port.port_number}${deletePort.port.connection ? ' (con conexión activa)' : ''}.`}
          saving={saving}
          onCancel={() => setDeletePort(null)}
          onConfirm={handleDeletePort}
        />
      )}

      {teamModal.open && (
        <TeamFormModal
          team={teamModal.team}
          nicPorts={nicPorts}
          saving={saving}
          onCancel={closeTeamModal}
          onSave={saveTeamModal}
        />
      )}
    </div>
  );
}

function ConfirmDialog({ title, message, saving, onCancel, onConfirm }) {
  return (
    <div className="cmp-confirm-overlay">
      <div className="cmp-confirm">
        <h4>{title}</h4>
        <p>{message}</p>
        <div className="cmp-confirm-actions">
          <button className="cmp-btn-secondary" onClick={onCancel} disabled={saving}>Cancelar</button>
          <button className="cmp-btn-danger" onClick={onConfirm} disabled={saving}>{saving ? '...' : 'Eliminar'}</button>
        </div>
      </div>
    </div>
  );
}

function TeamFormModal({ team, nicPorts, onSave, onCancel, saving }) {
  const [form, setForm] = useState(team || EMPTY_TEAM());

  useEffect(() => {
    setForm(team || EMPTY_TEAM());
  }, [team]);

  const toggleMember = (pid) => {
    const ids = (form.member_port_ids || []).map(Number);
    if (ids.includes(Number(pid))) setForm(f => ({ ...f, member_port_ids: ids.filter(x => x !== Number(pid)) }));
    else setForm(f => ({ ...f, member_port_ids: [...ids, Number(pid)] }));
  };

  return (
    <div className="cmp-form-overlay" onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="cmp-form-modal" style={{ maxWidth: 860 }}>
        <div className="cmp-form-header">
          <h4>{form?.name ? 'Editar team' : 'Nuevo team'}</h4>
          <button className="cmp-close" onClick={onCancel}>✕</button>
        </div>
        <div className="cmp-form-body">
          <div className="cmp-form-row">
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Nombre team</label>
              <input className="cmp-input" value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="bond0 / team0" />
            </div>
            <div className="cmp-form-field">
              <label>Modo</label>
              <select className="cmp-input" value={form.mode || 'lacp'} onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}>
                {TEAM_MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="cmp-form-field">
              <label>VLAN ID</label>
              <input className="cmp-input" type="number" min="1" max="4094" value={form.vlan_id || ''} onChange={e => setForm(f => ({ ...f, vlan_id: e.target.value }))} placeholder="10" />
            </div>
            <div className="cmp-form-field">
              <label>MTU</label>
              <input className="cmp-input" type="number" min="576" max="9216" value={form.mtu || ''} onChange={e => setForm(f => ({ ...f, mtu: e.target.value }))} placeholder="1500" />
            </div>
          </div>

          <div className="cmp-form-row">
            <div className="cmp-form-field" style={{ flex: '0 0 150px' }}>
              <label>IP mode</label>
              <select className="cmp-input" value={form.ip_mode || ''} onChange={e => setForm(f => ({ ...f, ip_mode: e.target.value }))}>
                <option value="">—</option>
                <option value="dhcp">dhcp</option>
                <option value="static">static</option>
                <option value="none">none</option>
              </select>
            </div>
            <div className="cmp-form-field cmp-form-field--grow">
              <label>IP</label>
              <input className="cmp-input" value={form.ip_address || ''} onChange={e => setForm(f => ({ ...f, ip_address: e.target.value }))} placeholder="192.168.1.10" />
            </div>
            <div className="cmp-form-field" style={{ flex: '0 0 100px' }}>
              <label>Prefix</label>
              <input className="cmp-input" type="number" min="1" max="32" value={form.prefix || ''} onChange={e => setForm(f => ({ ...f, prefix: e.target.value }))} placeholder="24" />
            </div>
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Gateway</label>
              <input className="cmp-input" value={form.gateway || ''} onChange={e => setForm(f => ({ ...f, gateway: e.target.value }))} placeholder="192.168.1.1" />
            </div>
          </div>

          <div className="cmp-form-field">
            <label>DNS</label>
            <input className="cmp-input" value={form.dns || ''} onChange={e => setForm(f => ({ ...f, dns: e.target.value }))} placeholder="8.8.8.8,1.1.1.1" />
          </div>

          <div className="cmp-form-field">
            <label>Miembros del team (puertos NIC)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, border: '1px solid var(--border)', borderRadius: 8, padding: 8, maxHeight: 180, overflow: 'auto', background: 'var(--bg-input)' }}>
              {nicPorts.length === 0 ? (
                <span style={{ color: 'var(--text-muted)' }}>Sin puertos NIC disponibles.</span>
              ) : (
                nicPorts.map(p => {
                  const checked = (form.member_port_ids || []).map(Number).includes(Number(p.id));
                  return (
                    <label key={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px', borderRadius: 6, border: `1px solid ${checked ? '#8b5cf6' : 'var(--border)'}`, background: checked ? 'rgba(99,102,241,.2)' : 'var(--bg-toolbar)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleMember(p.id)} />
                      <span style={{ fontSize: '0.78rem', color: 'var(--text-primary)' }}>{p.componentName} · {p.label}{p.speed ? ` · ${p.speed}` : ''}</span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <div className="cmp-form-actions">
            <button type="button" className="cmp-btn-secondary" onClick={onCancel} disabled={saving}>Cancelar</button>
            <button type="button" className="cmp-btn-primary" onClick={() => onSave(form)} disabled={saving}>
              {saving ? '⏳...' : <><CheckIcon style={{ width: 14, height: 14 }} /> Guardar team</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── CopyToServerModal ────────────────────────────────────────────────────────

const COMP_ICONS = { cpu: '💠', ram: '🟦', nic: '🔌', hba: '🌀', gpu: '🎮', raid: '💾', psu: '⚡', bmc: '🔧', other: '🔧' };

function stripComponent(comp) {
  // Remove hardware-unique identifiers before cloning to another server
  const { serial_number, asset_tag, id, element_id, ports, created_at, updated_at, ...rest } = comp;
  return rest;
}

function stripPort(port) {
  const { mac_address, wwn, id, component_id, connection, created_at, updated_at, ...rest } = port;
  return rest;
}

function CopyToServerModal({ sourceServer, components, companyId, onClose, onDone }) {
  const [servers,    setServers]    = useState([]);
  const [loadingSrv, setLoadingSrv] = useState(true);
  const [search,     setSearch]     = useState('');
  const [targetId,   setTargetId]   = useState(null);
  const [selected,   setSelected]   = useState(() => new Set(components.map(c => c.id)));
  const [copying,    setCopying]    = useState(false);
  const [progress,   setProgress]   = useState(null); // { done, total, current }
  const [result,     setResult]     = useState(null);  // { ok, errors }

  useEffect(() => {
    api.getServers(companyId)
      .then(res => {
        const list = (res.data || []).filter(s => s.id !== sourceServer.id);
        setServers(list);
      })
      .catch(() => setServers([]))
      .finally(() => setLoadingSrv(false));
  }, [companyId, sourceServer.id]);

  const filtered = servers.filter(s => {
    const q = search.toLowerCase();
    return !q || (s.name || s.hostname || '').toLowerCase().includes(q);
  });

  const toggleComp = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === components.length) setSelected(new Set());
    else setSelected(new Set(components.map(c => c.id)));
  };

  const handleCopy = async () => {
    if (!targetId) return;
    const toCopy = components.filter(c => selected.has(c.id));
    if (!toCopy.length) return;
    setCopying(true);
    setProgress({ done: 0, total: toCopy.length, current: '' });
    const errors = [];
    let done = 0;
    for (const comp of toCopy) {
      setProgress(p => ({ ...p, current: `${comp.manufacturer || ''} ${comp.model || comp.component_type}`.trim() }));
      try {
        const payload = stripComponent(comp);
        const res = await api.createComponent(targetId, payload, companyId);
        const newCompId = res.data?.id;
        if (newCompId && comp.component_type === 'nic' && comp.ports?.length) {
          for (const port of comp.ports) {
            try {
              await api.createComponentPort(targetId, newCompId, stripPort(port), companyId);
            } catch (pe) {
              errors.push(`Puerto ${port.label || port.port_number}: ${pe.message}`);
            }
          }
        }
      } catch (e) {
        errors.push(`${comp.manufacturer || ''} ${comp.model || comp.component_type}: ${e.message}`);
      }
      done++;
      setProgress(p => ({ ...p, done }));
    }
    setResult({ ok: done - errors.length, errors });
    setCopying(false);
    onDone?.();
  };

  const targetName = servers.find(s => s.id === targetId)?.name || servers.find(s => s.id === targetId)?.hostname || '';
  const selCount = selected.size;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 3000,
      background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={e => { if (e.target === e.currentTarget && !copying) onClose(); }}>
      <div style={{
        background: 'var(--bg-surface)', borderRadius: 14, boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
        border: '1px solid var(--border)', color: 'var(--text-primary)',
        width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* header */}
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <ArrowTopRightOnSquareIcon style={{ width: 20, height: 20, color: '#7c3aed' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '0.95rem' }}>Copiar componentes a otro servidor</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Origen: <strong>{sourceServer.name || sourceServer.hostname}</strong> — Se omitirán nº serie, asset tag, MACs y WWNs</div>
          </div>
          {!copying && <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--text-muted)' }}>✕</button>}
        </div>

        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

          {/* left: server picker */}
          <div style={{ width: 240, borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
            <div style={{ padding: '0.6rem 0.8rem', borderBottom: '1px solid #f1f5f9' }}>
              <input
                className="form-control form-control-sm"
                placeholder="Buscar servidor…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {loadingSrv ? (
                <div style={{ padding: '1rem', color: '#94a3b8', fontSize: '0.8rem' }}>Cargando…</div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: '1rem', color: '#94a3b8', fontSize: '0.8rem' }}>Sin servidores disponibles.</div>
              ) : (
                filtered.map(s => (
                  <button key={s.id} type="button" onClick={() => setTargetId(s.id)}
                    style={{
                      width: '100%', textAlign: 'left', padding: '0.5rem 0.8rem',
                      background: targetId === s.id ? '#ede9fe' : 'transparent',
                      border: 'none', borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                      color: targetId === s.id ? '#5b21b6' : '#334155',
                      fontWeight: targetId === s.id ? 700 : 400, fontSize: '0.8rem',
                    }}
                  >
                    {targetId === s.id && <span style={{ marginRight: 4 }}>●</span>}
                    {s.name || s.hostname}
                    {s.hostname && s.name && s.name !== s.hostname && (
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{s.hostname}</div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* right: component checklist */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {result ? (
              // result summary
              <div style={{ padding: '1.5rem', flex: 1 }}>
                <div style={{ fontSize: '1.3rem', marginBottom: 8 }}>{result.errors.length === 0 ? '✅' : '⚠️'}</div>
                <div style={{ fontWeight: 700, color: '#1e293b', marginBottom: 6 }}>
                  {result.ok} componente(s) copiados a <em>{targetName}</em>
                </div>
                {result.errors.length > 0 && (
                  <div style={{ background: '#fee2e2', borderRadius: 6, padding: '0.6rem 0.8rem' }}>
                    <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: 4, fontSize: '0.8rem' }}>Errores:</div>
                    {result.errors.map((e, i) => <div key={i} style={{ fontSize: '0.78rem', color: '#7f1d1d' }}>• {e}</div>)}
                  </div>
                )}
                <button
                  type="button" onClick={onClose}
                  style={{ marginTop: 16, padding: '7px 20px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}
                >Cerrar</button>
              </div>
            ) : (
              <>
                <div style={{ padding: '0.6rem 0.9rem', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={selCount === components.length} onChange={toggleAll} />
                    <span style={{ fontWeight: 600 }}>Todos ({selCount}/{components.length})</span>
                  </label>
                  <span style={{ fontSize: '0.74rem', color: '#94a3b8', marginLeft: 'auto' }}>sin nº serie · sin asset tag · sin MAC · sin WWN</span>
                </div>
                <div style={{ overflowY: 'auto', flex: 1, padding: '0.5rem 0.7rem' }}>
                  {components.map(comp => (
                    <label key={comp.id} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8, padding: '0.45rem 0.4rem',
                      borderRadius: 7, cursor: 'pointer', userSelect: 'none',
                      background: selected.has(comp.id) ? '#faf5ff' : 'transparent',
                      border: `1px solid ${selected.has(comp.id) ? '#ddd6fe' : 'transparent'}`,
                      marginBottom: 4, transition: 'all 0.1s',
                    }}>
                      <input type="checkbox" checked={selected.has(comp.id)} onChange={() => toggleComp(comp.id)} style={{ marginTop: 2 }} />
                      <span style={{ fontSize: '1.1rem', lineHeight: 1.2 }}>{COMP_ICONS[comp.component_type] || '🔧'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e293b' }}>
                          {comp.manufacturer || ''} {comp.model || <em style={{ color: '#94a3b8' }}>Sin modelo</em>}
                        </div>
                        <div style={{ fontSize: '0.73rem', color: '#64748b' }}>
                          {COMP_LABELS[comp.component_type]}{comp.slot ? ` · slot ${comp.slot}` : ''}
                          {comp.component_type === 'nic' && comp.ports?.length ? ` · ${comp.ports.length} puerto(s)` : ''}
                          {comp.component_type === 'psu' && comp.power_watts ? ` · ${comp.power_watts}W` : ''}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 1 }}>
                          {comp.serial_number && <span style={{ textDecoration: 'line-through' }}>S/N: {comp.serial_number}</span>}
                          {comp.serial_number && comp.asset_tag && ' · '}
                          {comp.asset_tag && <span style={{ textDecoration: 'line-through' }}>Tag: {comp.asset_tag}</span>}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                {/* progress bar while copying */}
                {copying && progress && (
                  <div style={{ padding: '0.6rem 0.9rem', borderTop: '1px solid var(--border)', background: 'var(--bg-toolbar)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.76rem', color: 'var(--text-secondary)', marginBottom: 3 }}>
                      <span>Copiando: <em>{progress.current}</em></span>
                      <span>{progress.done}/{progress.total}</span>
                    </div>
                    <div style={{ height: 5, background: 'rgba(255,255,255,.12)', borderRadius: 3 }}>
                      <div style={{ height: '100%', background: '#7c3aed', borderRadius: 3, transition: 'width 0.2s', width: `${(progress.done / progress.total) * 100}%` }} />
                    </div>
                  </div>
                )}

                {/* footer */}
                <div style={{ padding: '0.75rem 1rem', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                  {!targetId && <span style={{ fontSize: '0.78rem', color: '#f59e0b' }}>← Selecciona un servidor destino</span>}
                  {targetId && <span style={{ fontSize: '0.78rem', color: '#059669' }}>Destino: <strong>{targetName}</strong></span>}
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <button type="button" onClick={onClose} disabled={copying}
                      style={{ padding: '6px 14px', borderRadius: 7, background: 'var(--bg-toolbar)', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '0.82rem' }}
                    >Cancelar</button>
                    <button type="button" onClick={handleCopy}
                      disabled={!targetId || selCount === 0 || copying}
                      style={{
                        padding: '6px 18px', borderRadius: 7, fontWeight: 700, fontSize: '0.82rem',
                        background: targetId && selCount > 0 ? '#7c3aed' : '#e2e8f0',
                        color: targetId && selCount > 0 ? '#fff' : '#94a3b8',
                        border: 'none', cursor: targetId && selCount > 0 ? 'pointer' : 'not-allowed',
                        opacity: copying ? 0.6 : 1,
                      }}
                    >
                      {copying ? '⏳ Copiando…' : `Copiar ${selCount} componente(s)`}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ComponentForm ─────────────────────────────────────────────────────────────

// Quick presets: [label, portCount, portType, speed]
const NIC_PRESETS = [
  ['4×RJ45 1G',     4, 'rj45',   '1G'  ],
  ['2×RJ45 1G',     2, 'rj45',   '1G'  ],
  ['2×SFP+ 10G',    2, 'sfp+',   '10G' ],
  ['4×SFP+ 10G',    4, 'sfp+',   '10G' ],
  ['2×SFP28 25G',   2, 'sfp+',   '25G' ],
  ['2×QSFP28 100G', 2, 'qsfp28', '100G'],
  ['1×QSFP28 100G', 1, 'qsfp28', '100G'],
];
const HBA_PRESETS = [
  ['2×FC 8G',  2, 'fc', '8G' ],
  ['2×FC 16G', 2, 'fc', '16G'],
  ['4×FC 16G', 4, 'fc', '16G'],
  ['2×FC 32G', 2, 'fc', '32G'],
];
const PORT_TYPES_NIC = ['rj45', 'sfp', 'sfp+', 'qsfp', 'qsfp28'];
const PORT_TYPES_HBA = ['fc', 'sfp', 'sfp+'];
const PORT_SPEEDS_NIC = ['100M', '1G', '2.5G', '10G', '25G', '40G', '100G'];
const PORT_SPEEDS_HBA = ['4G', '8G', '16G', '32G', '64G'];

// Map component types to catalog device_type values
const COMP_TYPE_TO_DEVICE_TYPE = {
  nic: 'nic', hba: 'hba', gpu: 'gpu', cpu: 'cpu',
  ram: 'ram', raid: 'raid', psu: 'psu', other: null,
};

function ComponentForm({ comp, companyId, onSave, onCancel, saving }) {
  const isEdit = Boolean(comp?.id);
  const [form, setForm] = useState({
    component_type: comp?.component_type || 'nic',
    slot: comp?.slot || '',
    manufacturer: comp?.manufacturer || '',
    model: comp?.model || '',
    serial_number: comp?.serial_number || '',
    asset_tag: comp?.asset_tag || '',
    status: comp?.status || 'active',
    notes: comp?.notes || '',
    power_watts: comp?.power_watts || '',
    connector_type: comp?.connector_type || '',
    efficiency: comp?.efficiency || '',
  });

  // Catalog dropdowns
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [addingManufacturer, setAddingManufacturer] = useState(false);
  const [newManufacturer, setNewManufacturer] = useState('');
  const [addingModel, setAddingModel] = useState(false);
  const [newModel, setNewModel] = useState('');

  const deviceType = COMP_TYPE_TO_DEVICE_TYPE[form.component_type] || null;

  const loadCatalog = useCallback(async (dt) => {
    if (!companyId) return;
    try {
      const opts = dt ? { deviceType: dt } : {};
      const [mfrRes, modelRes] = await Promise.all([
        api.getManufacturers(companyId, opts).catch(() => ({ data: [] })),
        api.getDeviceModels(companyId, opts).catch(() => ({ data: [] })),
      ]);
      setManufacturerOptions(Array.isArray(mfrRes?.data) ? mfrRes.data : []);
      setModelOptions(Array.isArray(modelRes?.data) ? modelRes.data : []);
    } catch (_) { /* ignore */ }
  }, [companyId]);

  useEffect(() => { loadCatalog(deviceType); }, [loadCatalog, deviceType]);

  const filteredModelOptions = useMemo(() => {
    const matched = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    const mid = matched?.id;
    return (modelOptions || []).filter(m => !mid || m.manufacturer_id === mid || !m.manufacturer_id);
  }, [modelOptions, manufacturerOptions, form.manufacturer]);

  const saveManufacturerToCatalog = async (name) => {
    if (!name || !companyId) return;
    try {
      await api.createManufacturer({ name, device_type: deviceType }, companyId);
      await loadCatalog(deviceType);
    } catch (_) { /* ignore */ }
  };

  const saveModelToCatalog = async (modelName) => {
    if (!modelName || !companyId) return;
    const matched = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    try {
      await api.createDeviceModel({ name: modelName, device_type: deviceType, manufacturer_id: matched?.id }, companyId);
      await loadCatalog(deviceType);
    } catch (_) { /* ignore */ }
  };

  // Auto-ports state (only for new NIC/HBA)
  const [autoPorts, setAutoPorts] = useState({ enabled: true, count: '2', port_type: 'rj45', speed: '1G' });

  const isPsu = form.component_type === 'psu';
  const isNic = form.component_type === 'nic';
  const isHba = form.component_type === 'hba';
  const showAutoPorts = !isEdit && (isNic || isHba);

  // Reset auto-ports defaults and catalog when type changes
  const handleTypeChange = (newType) => {
    setForm(f => ({ ...f, component_type: newType, manufacturer: '', model: '' }));
    setAddingManufacturer(false); setNewManufacturer('');
    setAddingModel(false); setNewModel('');
    if (newType === 'hba') setAutoPorts({ enabled: true, count: '2', port_type: 'fc', speed: '16G' });
    else if (newType === 'nic') setAutoPorts({ enabled: true, count: '2', port_type: 'rj45', speed: '1G' });
  };

  const applyPreset = ([, count, port_type, speed]) => {
    setAutoPorts(a => ({ ...a, count: String(count), port_type, speed }));
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    // Send PSU fields only when type is psu; clean empty strings
    if (!isPsu) {
      delete payload.power_watts;
      delete payload.connector_type;
      delete payload.efficiency;
    } else {
      payload.power_watts = payload.power_watts ? parseInt(payload.power_watts, 10) : null;
      payload.connector_type = payload.connector_type || null;
      payload.efficiency = payload.efficiency || null;
    }
    // Attach auto-ports template for the parent to consume
    if (showAutoPorts && autoPorts.enabled && Number(autoPorts.count) > 0) {
      payload._autoPortsTemplate = {
        count: Number(autoPorts.count),
        port_type: autoPorts.port_type,
        speed: autoPorts.speed,
      };
    }
    onSave(payload);
  };

  return (
    <div className="cmp-form-overlay" onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="cmp-form-modal">
        <div className="cmp-form-header">
          <h4>{isEdit ? 'Editar componente' : 'Nuevo componente'}</h4>
          <button className="cmp-close" onClick={onCancel}>✕</button>
        </div>
        <form className="cmp-form-body" onSubmit={submit}>
          <div className="cmp-form-row">
            <div className="cmp-form-field">
              <label>Tipo</label>
              <select className="cmp-input" value={form.component_type} onChange={e => handleTypeChange(e.target.value)}>
                {COMP_TYPES.map(t => <option key={t} value={t}>{COMP_LABELS[t]}</option>)}
              </select>
            </div>
            <div className="cmp-form-field">
              <label>Slot / Posición</label>
              <input className="cmp-input" value={form.slot} onChange={e => setForm(f => ({ ...f, slot: e.target.value }))} placeholder={isPsu ? 'PSU-A, PSU-B' : 'PCIe-2'} />
            </div>
          </div>
          <div className="cmp-form-row">
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Fabricante</label>
              <div style={{ display: 'flex', gap: 4 }}>
                <select
                  className="cmp-input"
                  style={{ flex: 1 }}
                  value={form.manufacturer}
                  onChange={e => {
                    setForm(f => ({ ...f, manufacturer: e.target.value, model: '' }));
                    setAddingManufacturer(false); setNewManufacturer('');
                    setAddingModel(false); setNewModel('');
                  }}
                >
                  <option value="">— Seleccionar —</option>
                  {manufacturerOptions.map(opt => (
                    <option key={opt.id || opt.name} value={opt.name}>{opt.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="cmp-btn-secondary"
                  style={{ whiteSpace: 'nowrap', padding: '0 8px' }}
                  onClick={() => setAddingManufacturer(v => !v)}
                  title="Añadir al catálogo"
                >+ Nuevo</button>
              </div>
              {addingManufacturer && (
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                  <input
                    className="cmp-input"
                    style={{ flex: 1 }}
                    placeholder="Nombre del fabricante"
                    value={newManufacturer}
                    onChange={e => setNewManufacturer(e.target.value)}
                  />
                  <button
                    type="button"
                    className="cmp-btn-primary"
                    style={{ padding: '0 8px' }}
                    onClick={async () => {
                      if (!newManufacturer.trim()) return;
                      const name = newManufacturer.trim();
                      setForm(f => ({ ...f, manufacturer: name }));
                      await saveManufacturerToCatalog(name);
                      setNewManufacturer(''); setAddingManufacturer(false);
                    }}
                  >Guardar</button>
                  <button
                    type="button"
                    className="cmp-btn-secondary"
                    style={{ padding: '0 8px' }}
                    onClick={() => { setAddingManufacturer(false); setNewManufacturer(''); }}
                  >✕</button>
                </div>
              )}
            </div>
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Modelo</label>
              <div style={{ display: 'flex', gap: 4 }}>
                <select
                  className="cmp-input"
                  style={{ flex: 1 }}
                  value={form.model}
                  onChange={e => { setForm(f => ({ ...f, model: e.target.value })); setAddingModel(false); setNewModel(''); }}
                >
                  <option value="">— Seleccionar —</option>
                  {filteredModelOptions.map(opt => (
                    <option key={opt.id || opt.name} value={opt.name}>{opt.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="cmp-btn-secondary"
                  style={{ whiteSpace: 'nowrap', padding: '0 8px' }}
                  onClick={() => setAddingModel(v => !v)}
                  title="Añadir al catálogo"
                >+ Nuevo</button>
              </div>
              {addingModel && (
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                  <input
                    className="cmp-input"
                    style={{ flex: 1 }}
                    placeholder="Nombre del modelo"
                    value={newModel}
                    onChange={e => setNewModel(e.target.value)}
                  />
                  <button
                    type="button"
                    className="cmp-btn-primary"
                    style={{ padding: '0 8px' }}
                    onClick={async () => {
                      if (!newModel.trim()) return;
                      const name = newModel.trim();
                      setForm(f => ({ ...f, model: name }));
                      await saveModelToCatalog(name);
                      setNewModel(''); setAddingModel(false);
                    }}
                  >Guardar</button>
                  <button
                    type="button"
                    className="cmp-btn-secondary"
                    style={{ padding: '0 8px' }}
                    onClick={() => { setAddingModel(false); setNewModel(''); }}
                  >✕</button>
                </div>
              )}
            </div>
          </div>

          {/* PSU-specific fields */}
          {isPsu && (
            <div className="cmp-form-row" style={{ background: 'rgba(245,158,11,.1)', borderRadius: 6, padding: '0.5rem 0.75rem', margin: '0.25rem 0' }}>
              <div className="cmp-form-field" style={{ flex: '0 0 120px' }}>
                <label>⚡ Vatios (W)</label>
                <input className="cmp-input" type="number" min="0" value={form.power_watts} onChange={e => setForm(f => ({ ...f, power_watts: e.target.value }))} placeholder="750" />
              </div>
              <div className="cmp-form-field" style={{ flex: '0 0 160px' }}>
                <label>Conector</label>
                <select className="cmp-input" value={form.connector_type} onChange={e => setForm(f => ({ ...f, connector_type: e.target.value }))}>
                  <option value="">— Sin especificar —</option>
                  <option value="IEC C14">IEC C14 (estándar)</option>
                  <option value="IEC C20">IEC C20 (alta potencia)</option>
                  <option value="Schuko">Schuko (CEE 7/4)</option>
                  <option value="IEC 309">IEC 309 (industrial)</option>
                </select>
              </div>
              <div className="cmp-form-field" style={{ flex: '0 0 150px' }}>
                <label>Eficiencia</label>
                <select className="cmp-input" value={form.efficiency} onChange={e => setForm(f => ({ ...f, efficiency: e.target.value }))}>
                  <option value="">—</option>
                  <option value="80+ White">80+ White</option>
                  <option value="80+ Bronze">80+ Bronze</option>
                  <option value="80+ Silver">80+ Silver</option>
                  <option value="80+ Gold">80+ Gold</option>
                  <option value="80+ Platinum">80+ Platinum</option>
                  <option value="80+ Titanium">80+ Titanium</option>
                </select>
              </div>
            </div>
          )}

          {/* Auto-ports panel — only for new NIC / HBA */}
          {showAutoPorts && (
            <div style={{ background: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.2)', borderRadius: 8, padding: '0.65rem 0.85rem', margin: '0.25rem 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: '0.82rem', color: '#818cf8', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={autoPorts.enabled}
                    onChange={e => setAutoPorts(a => ({ ...a, enabled: e.target.checked }))}
                  />
                  {isNic ? '🔌 Crear puertos NIC automáticamente' : '🔌 Crear puertos HBA automáticamente'}
                </label>
                <span style={{ fontSize: '0.73rem', color: 'rgba(255,255,255,.4)' }}>Presets rápidos:</span>
              </div>
              {/* Preset chips */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: autoPorts.enabled ? 10 : 0 }}>
                {(isHba ? HBA_PRESETS : NIC_PRESETS).map(preset => (
                  <button
                    key={preset[0]}
                    type="button"
                    onClick={() => { applyPreset(preset); setAutoPorts(a => ({ ...a, enabled: true })); }}
                    style={{
                      padding: '3px 9px', fontSize: '0.72rem', borderRadius: 20, cursor: 'pointer',
                      border: `1px solid ${autoPorts.count === String(preset[1]) && autoPorts.port_type === preset[2] && autoPorts.speed === preset[3] ? '#6366f1' : 'rgba(99,102,241,.25)'}`,
                      background: autoPorts.count === String(preset[1]) && autoPorts.port_type === preset[2] && autoPorts.speed === preset[3] ? 'rgba(99,102,241,.25)' : 'rgba(255,255,255,.04)',
                      color: '#a5b4fc', fontWeight: 600,
                    }}
                  >{preset[0]}</button>
                ))}
              </div>
              {/* Port config fields */}
              {autoPorts.enabled && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <div className="cmp-form-field" style={{ flex: '0 0 90px' }}>
                    <label>Nº puertos</label>
                    <select className="cmp-input" value={autoPorts.count} onChange={e => setAutoPorts(a => ({ ...a, count: e.target.value }))}>
                      <option value="1">1 puerto</option>
                      <option value="2">2 puertos</option>
                      <option value="4">4 puertos</option>
                    </select>
                  </div>
                  <div className="cmp-form-field" style={{ flex: '0 0 130px' }}>
                    <label>Tipo de puerto</label>
                    <select className="cmp-input" value={autoPorts.port_type} onChange={e => setAutoPorts(a => ({ ...a, port_type: e.target.value }))}>
                      {(isHba ? PORT_TYPES_HBA : PORT_TYPES_NIC).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="cmp-form-field" style={{ flex: '0 0 120px' }}>
                    <label>Velocidad</label>
                    <select className="cmp-input" value={autoPorts.speed} onChange={e => setAutoPorts(a => ({ ...a, speed: e.target.value }))}>
                      {(isHba ? PORT_SPEEDS_HBA : PORT_SPEEDS_NIC).map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 4 }}>
                    <span style={{ fontSize: '0.75rem', color: '#818cf8', background: 'rgba(99,102,241,.18)', borderRadius: 5, padding: '3px 8px' }}>
                      Se crearán <strong>{autoPorts.count}</strong> puertos · <strong>{autoPorts.port_type}</strong> · <strong>{autoPorts.speed}</strong>
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="cmp-form-row">
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Nº serie</label>
              <input className="cmp-input" value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} />
            </div>
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Asset tag</label>
              <input className="cmp-input" value={form.asset_tag} onChange={e => setForm(f => ({ ...f, asset_tag: e.target.value }))} />
            </div>
            <div className="cmp-form-field" style={{ flex: '0 0 140px' }}>
              <label>Estado</label>
              <select className="cmp-input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="active">Activo</option>
                <option value="faulty">Averiado</option>
                <option value="spare">Repuesto</option>
              </select>
            </div>
          </div>
          <div className="cmp-form-field">
            <label>Notas</label>
            <textarea className="cmp-input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="cmp-form-actions">
            <button type="button" className="cmp-btn-secondary" onClick={onCancel} disabled={saving}>Cancelar</button>
            <button type="submit" className="cmp-btn-primary" disabled={saving}>{saving ? '⏳...' : isEdit ? '💾 Guardar' : '+ Añadir'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PortForm({ editPort, onSave, onCancel, saving }) {
  const { compId, port } = editPort;
  const isEdit = Boolean(port?.id);
  const [form, setForm] = useState({
    port_number: port?.port_number || '',
    label: port?.label || '',
    port_type: port?.port_type || 'rj45',
    speed: port?.speed || '',
    mac_address: port?.mac_address || '',
    wwn: port?.wwn || '',
    notes: port?.notes || '',
    is_active: port?.is_active ?? true,
  });

  const submit = (e) => {
    e.preventDefault();
    onSave(compId, port?.id || null, {
      ...form,
      port_number: form.port_number ? Number(form.port_number) : undefined,
    });
  };

  return (
    <div className="cmp-form-overlay" onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="cmp-form-modal">
        <div className="cmp-form-header">
          <h4>{isEdit ? 'Editar puerto' : 'Nuevo puerto'}</h4>
          <button className="cmp-close" onClick={onCancel}>✕</button>
        </div>
        <form className="cmp-form-body" onSubmit={submit}>
          <div className="cmp-form-row">
            <div className="cmp-form-field" style={{ flex: '0 0 100px' }}>
              <label>Número</label>
              <input className="cmp-input" type="number" min="0" value={form.port_number} onChange={e => setForm(f => ({ ...f, port_number: e.target.value }))} disabled={isEdit} />
            </div>
            <div className="cmp-form-field cmp-form-field--grow">
              <label>Label</label>
              <input className="cmp-input" value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))} placeholder="eth0" />
            </div>
            <div className="cmp-form-field">
              <label>Tipo</label>
              <select className="cmp-input" value={form.port_type} onChange={e => setForm(f => ({ ...f, port_type: e.target.value }))}>
                {PORT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="cmp-form-field">
              <label>Velocidad</label>
              <select className="cmp-input" value={form.speed} onChange={e => setForm(f => ({ ...f, speed: e.target.value }))}>
                {PORT_SPEEDS.map(s => <option key={s} value={s}>{s || '—'}</option>)}
              </select>
            </div>
          </div>
          <div className="cmp-form-row">
            <div className="cmp-form-field cmp-form-field--grow">
              <label>MAC</label>
              <input className="cmp-input" value={form.mac_address} onChange={e => setForm(f => ({ ...f, mac_address: e.target.value }))} placeholder="00:11:22:33:44:55" />
            </div>
            <div className="cmp-form-field cmp-form-field--grow">
              <label>WWN</label>
              <input className="cmp-input" value={form.wwn} onChange={e => setForm(f => ({ ...f, wwn: e.target.value }))} placeholder="50:01:43:..." />
            </div>
          </div>
          <div className="cmp-form-field">
            <label>Notas</label>
            <textarea className="cmp-input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="cmp-form-actions">
            <button type="button" className="cmp-btn-secondary" onClick={onCancel} disabled={saving}>Cancelar</button>
            <button type="submit" className="cmp-btn-primary" disabled={saving}>{saving ? '⏳...' : isEdit ? '💾 Guardar' : '+ Añadir'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ServerComponentsPanel;
